import { useProfiles } from "@megarax/features";
import React from "react";

import type { MessageDto, TicketDetailDto } from "@megaron/support-contracts";

import { toDisplayName } from "../shared/displayName";
import { getSupportTicketCreatedByEmail } from "./getSupportTicketCreatedByEmail";
import { SupportTicketEmailEntry } from "./SupportTicketEmailEntry";
import { SupportTicketFormEntry } from "./SupportTicketFormEntry";
import { shouldRenderSupportTicketFormEntry } from "./supportTicketMessageEntryKind";

type MessageTimelineItem = { kind: "message"; data: MessageDto; date: Date };

type Props = {
  ticket: TicketDetailDto;
  messageTimeline: MessageTimelineItem[];
  formatDate: (date: Date) => string;
};

export const SupportTicketMessageTimeline: React.FC<Props> = ({ ticket, messageTimeline, formatDate }) => {
  const { profile } = useProfiles();
  const createdByEmail = getSupportTicketCreatedByEmail(ticket);
  const createdByProfile = profile(createdByEmail);
  const enteredByName = createdByProfile
    ? `${createdByProfile.firstName} ${createdByProfile.lastName}`
    : createdByEmail
      ? toDisplayName(createdByEmail.split("@")[0])
      : "—";

  const contactName = ticket.reporterName?.trim() || ticket.reporterEmail.split("@")[0];

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {messageTimeline.map((item) => {
        if (shouldRenderSupportTicketFormEntry(ticket, item.data)) {
          return (
            <SupportTicketFormEntry
              key={item.data.id}
              ticketId={ticket.id}
              messageId={item.data.id}
              enteredByName={enteredByName}
              reporterName={ticket.reporterName}
              reporterEmail={ticket.reporterEmail}
              time={formatDate(item.date)}
              body={item.data.body}
              attachments={item.data.attachments}
            />
          );
        }

        const agentProfile = item.data.fromAgent ? profile(item.data.authorEmail ?? null) : null;
        const agentName = agentProfile
          ? `${agentProfile.firstName} ${agentProfile.lastName}`
          : item.data.authorEmail
            ? toDisplayName(item.data.authorEmail.split("@")[0])
            : "Agent";
        const fullName = item.data.fromAgent ? agentName : toDisplayName(contactName);

        return (
          <SupportTicketEmailEntry
            key={item.data.id}
            ticketId={ticket.id}
            messageId={item.data.id}
            fromName={fullName}
            fromEmail={item.data.fromAgent ? (item.data.authorEmail ?? "") : ticket.reporterEmail}
            time={formatDate(item.date)}
            body={item.data.body}
            isOutgoing={item.data.fromAgent}
            attachments={item.data.attachments}
          />
        );
      })}
    </div>
  );
};
